import md5 from 'md5';

/**
 * Remove whitespace from start and end of string
 * @param {String} str
 */
export const strim = value => {
    if (!(typeof value === 'string')) {
        return '';
    }
    return value.trim().replace(/\s\s+/g, ' ');
};

/**
 * Hashes an item using MD5.
 * @param {any} item - The item to hash.
 * @param {Number} length - The desired length of the hash.
 * @returns {String} The MD5 hash of the item.
 */
export const hash = (item, length = 18) => {
    if (item === null || item === undefined) {
        item = {};
    }

    return md5(JSON.stringify(item)).slice(0, length);
};

/**
 * Extracts text content from an HTML string.
 * @param {String} html - The HTML string to extract text from.
 * @returns {String} The plain text content.
 */
export const extractTextFromHtml = html => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

/**
 * Obfuscates an item (string or object) by replacing specific characters and encoding it.
 * @param {String|Object} item - The item to obfuscate.
 * @returns {String} The obfuscated string.
 */
export const obfuscate = item => {
    if (typeof item === 'object') {
        item = JSON.stringify(item);
    }

    item = item.replace(/a/g, '%Jx3b),');
    item = item.replace(/{/g, '[~r2L');
    item = item.replace(/:/g, 'Ap0Y|^');
    item = item.replace(/_/g, 'redacted*L16');
    item = item.replace(/~/g, 'd7:{a_');

    item = encodeURI(item);

    return item;
};

/**
 * Unobfuscates an item (string or object) by decoding it and replacing specific characters.
 * @param {String} item - The item to unobfuscate.
 * @returns {String|Object} The unobfuscated string or object.
 */
export const unobfuscate = item => {
    item = decodeURI(item);

    item = item.replace(/d7:{a_/g, '~');
    item = item.replace(/redacted\*L16/g, '_');
    item = item.replace(/Ap0Y\|\^/g, ':');
    item = item.replace(/\[~r2L/g, '{');
    item = item.replace(/%Jx3b\),/g, 'a');

    return JSON.parse(item);
};

/**
 * Generates a unique identifier of a specified length.
 * @param {Number} length - The desired length of the UUID.
 * @returns {String} The generated UUID.
 */

export const uuid = length => [...Array(length || 8)].map(() => (~~(Math.random() * 36)).toString(36)).join('');

/**
 * Extracts all string values recursively from an object or array
 * @param {Object|Array} obj - The object or array to extract strings from
 * @return {string[]} An array containing all unique strings found in the object or array
 * @throws {TypeError} If the input is not an object or array
 */
export const extractStrings = value => {
    let strings = new Set();

    if (typeof value === 'string') {
        strings.add(value);
    } else if (Array.isArray(value)) {
        value.forEach(item => {
            extractStrings(item).forEach(str => strings.add(str));
        });
    } else if (typeof value === 'object') {
        for (let key in value) {
            extractStrings(value[`${key}`]).forEach(str => strings.add(str));
        }
    }

    return [...strings];
};

/**
 * Formats a string based on the provided options.
 *
 * @param {string} stringValue - The input string to format.
 * @param {object} [options] - An object containing formatting options.
 * @param {boolean} [options.removeExtension=false] - If true, removes the file extension from the string.
 * @param {boolean} [options.removeSpecialCharacters=false] - If true, removes special characters
 * @param {boolean} [options.removeNumbers=false] - If true, removes numbers from the string.
 * @param {string|null} [options.replaceSpacesWith=null] - If a string, replaces all spaces with this string.
 * @param {Array<object>} [options.replacements=[]]
 * @param {boolean} [options.toLowerCase=false] - If true, converts the string to lowercase.
 * @param {boolean} [options.toUpperCase=false] - If true, converts the string to uppercase.
 * @param {boolean} [options.normalizeUnicode=true] - If true, normalizes unicode characters (e.g., 'é' becomes 'e').
 * @returns {string} The formatted string.
 */
export const formatString = (stringValue, options = {}) => {
    if (typeof stringValue !== 'string' || stringValue.trim() === '') {
        return '';
    }

    const defaultOptions = {
        removeExtension: false,
        removeSpecialCharacters: false,
        removeNumbers: false,
        replaceSpacesWith: null,
        replacements: [],
        toLowerCase: false,
        toUpperCase: false,
        normalizeUnicode: true
    };

    const mergedOptions = { ...defaultOptions, ...options };

    let cleanedName = stringValue;

    if (mergedOptions.normalizeUnicode) {
        cleanedName = cleanedName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    if (mergedOptions.toLowerCase) {
        cleanedName = cleanedName.toLowerCase();
    }

    if (mergedOptions.toUpperCase) {
        cleanedName = cleanedName.toUpperCase();
    }

    if (mergedOptions.removeExtension) {
        const lastDotIndex = cleanedName.lastIndexOf('.');
        if (lastDotIndex > 0) {
            // Ensure it's not a hidden file like '.bashrc'
            cleanedName = cleanedName.substring(0, lastDotIndex);
        }
    }

    if (mergedOptions.removeSpecialCharacters) {
        cleanedName = cleanedName.replace(/[^a-zA-Z0-9\s]/g, '');
    }

    if (mergedOptions.removeNumbers) {
        cleanedName = cleanedName.replace(/[0-9]/g, '');
    }

    if (Array.isArray(mergedOptions.replacements)) {
        for (const { find, replace } of mergedOptions.replacements) {
            if (find instanceof RegExp && typeof replace === 'string') {
                cleanedName = cleanedName.replace(find, replace);
            } else {
                console.warn(`Invalid replacement entry ignored: { find: ${find}, replace: ${replace} }.`);
            }
        }
    }

    if (typeof mergedOptions.replaceSpacesWith === 'string') {
        cleanedName = cleanedName.replace(/\s+/g, mergedOptions.replaceSpacesWith);
    } else {
        cleanedName = cleanedName.replace(/\s\s+/g, ' ');
    }

    cleanedName = cleanedName.trim();

    return cleanedName;
};

export const isEmailMatch = (email1 = '', email2 = '') => {
    if (!(typeof email1 === 'string') || !(typeof email2 === 'string')) {
        return false;
    }

    const clean = email => email.replaceAll(' ', '').toLowerCase();

    return clean(email1) === clean(email2);
};

/**
 * Converts a string to snake_case.
 * @param {String} str - The string to convert.
 * @returns {String} The snake_cased string.
 */
export const toSnakeCase = str => {
    if (typeof str !== 'string') {
        return '';
    }
    return str
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase();
};
